import type { ByteValue } from "utils/byteValues";

const DECIMAL_KB = BigInt(1000);
const BINARY_KIB = BigInt(1024);
const BINARY_GIB = BINARY_KIB * BINARY_KIB * BINARY_KIB;
const UNIT_FACTORS: Record<string, bigint> = {
  b: BigInt(1),
  kb: DECIMAL_KB,
  mb: DECIMAL_KB * DECIMAL_KB,
  gb: DECIMAL_KB * DECIMAL_KB * DECIMAL_KB,
  tb: DECIMAL_KB * DECIMAL_KB * DECIMAL_KB * DECIMAL_KB,
  kib: BINARY_KIB,
  mib: BINARY_KIB * BINARY_KIB,
  gib: BINARY_GIB,
  tib: BINARY_GIB * BINARY_KIB,
};

const formatDecimal = (value: number) =>
  value
    .toFixed(value >= 100 ? 1 : 2)
    .replace(/\.0+$/, "")
    .replace(/(\.\d*[1-9])0+$/, "$1");

const getBytesFromByteValue = (
  byteValue?: Partial<ByteValue> | null,
): bigint | undefined => {
  if (byteValue?.value == null || !byteValue.unit) return undefined;

  const valueText = String(byteValue.value).trim();
  if (!/^\d+$/.test(valueText)) return undefined;

  const factor = UNIT_FACTORS[byteValue.unit.trim().toLowerCase()];
  if (!factor) return undefined;

  return BigInt(valueText) * factor;
};

const getBytesFromMemoryText = (memory: string): bigint | undefined => {
  const compactText = memory.replace(/\s+/g, "");
  const validMemoryText = /^(\d+(?:TiB|GiB|MiB|KiB|PB|TB|GB|MB|KB|B))+$/i;
  if (!validMemoryText.test(compactText)) return undefined;

  let totalBytes = BigInt(0);
  for (const match of compactText.matchAll(
    /(\d+)(TiB|GiB|MiB|KiB|PB|TB|GB|MB|KB|B)/gi,
  )) {
    const factor = UNIT_FACTORS[match[2].toLowerCase()];
    if (!factor) return undefined;
    totalBytes += BigInt(match[1]) * factor;
  }

  return totalBytes;
};

export const formatGpuVramFromBytes = (
  memoryBytes?: bigint | null,
): string | undefined => {
  if (!memoryBytes || memoryBytes <= BigInt(0)) return undefined;

  if (memoryBytes % BINARY_GIB === BigInt(0)) {
    return `${memoryBytes / BINARY_GIB} GiB`;
  }

  return `${formatDecimal(Number(memoryBytes) / 1000 ** 3)} GB`;
};

export const formatGpuVramFromByteValue = (
  byteValue?: Partial<ByteValue> | null,
): string | undefined =>
  formatGpuVramFromBytes(getBytesFromByteValue(byteValue));

export const formatGpuVramText = (
  memory: string | undefined,
): string | undefined => {
  const text = memory?.trim();
  if (!text) return undefined;

  const memoryBytes = getBytesFromMemoryText(text);
  if (memoryBytes) return formatGpuVramFromBytes(memoryBytes);

  const match = text.match(/^(\d+(?:\.\d+)?)\s*(GiB|GB)$/i);
  if (!match) return text;

  const unit = match[2].toLowerCase() === "gib" ? "GiB" : "GB";
  return `${match[1]} ${unit}`;
};
